import { createFileRoute, Link } from "@tanstack/react-router";

import { PageHero, Prose, ProseSection } from "@/components/page-hero";

export const Route = createFileRoute("/copyright")({
  head: () => ({
    meta: [
      { title: "Copyright & Content Removal — Quibblies" },
      {
        name: "description",
        content:
          "How to report plagiarism or a copyright infringement on Quibblies, what a valid claim must contain, and how counter-notices and appeals work.",
      },
      { property: "og:title", content: "Copyright & Content Removal — Quibblies" },
      {
        property: "og:description",
        content:
          "A dedicated process for copyright claims, separate from ordinary community reports.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: CopyrightPage,
});

function CopyrightPage() {
  return (
    <div>
      <PageHero
        eyebrow="Copyright"
        title="Copyright and content removal"
        description="Claims that a member has copied someone else's work are handled through this process, separately from ordinary community reports."
      />
      <Prose>
        <ProseSection title="Before you file">
          <p>
            Similar premises, tropes and genre conventions are not copyright infringement. This
            process is for substantial copying of your own writing, artwork or profile media.
          </p>
        </ProseSection>
        <ProseSection title="What a valid claim contains">
          <p>Send the following to the Quibblies team:</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>A link to the Quibblies story, chapter, comment, profile or image concerned.</li>
            <li>A link to, or copy of, the original work you own.</li>
            <li>Which passages or elements were copied.</li>
            <li>Your name and contact email, and confirmation that you own the rights.</li>
            <li>A statement that the claim is made in good faith and is accurate.</li>
          </ul>
        </ProseSection>
        <ProseSection title="What happens next">
          <p>
            An administrator reviews the claim. Contested content may be hidden from public view
            while the review runs. The author is notified, may respond with a counter-notice, and
            may appeal the final decision to a senior administrator.
          </p>
        </ProseSection>
        <ProseSection title="Repeat infringement">
          <p>
            Accounts that repeatedly publish other people's work are suspended and then removed.
            Every action taken is written to an append-only audit record.
          </p>
        </ProseSection>
        <ProseSection title="Misuse of this process">
          <p>
            Filing knowingly false claims to silence another author is itself a guidelines breach
            and is actioned against the account that filed it.
          </p>
        </ProseSection>
        <ProseSection title="File a claim">
          <p>
            Use the{" "}
            <Link to="/contact" className="text-primary-glow underline-offset-4 hover:underline">
              contact form
            </Link>{" "}
            and choose a copyright topic, or report the item directly from the story or profile
            page.
          </p>
        </ProseSection>
      </Prose>
    </div>
  );
}
